import * as Sentry from '@sentry/react';

/**
 * Runtime configuration + Sentry (doc 13 "Monitoring and alerts", X-4).
 *
 * `public/env.js` defines `window.__SITEDAY_ENV__`; the Docker image rewrites it
 * at container start from VITE_API_URL / VITE_SENTRY_DSN (apps/web/docker/entrypoint.sh)
 * so one image serves staging and production. Build-time `import.meta.env.VITE_*`
 * values are the fallback (Vite dev server, static hosting).
 */
export interface RuntimeEnv {
  API_URL?: string;
  SENTRY_DSN?: string;
  RELEASE?: string;
  ENVIRONMENT?: string;
}

export function runtimeEnv(): RuntimeEnv {
  if (typeof window === 'undefined') return {};
  const raw = (window as Window & { __SITEDAY_ENV__?: unknown }).__SITEDAY_ENV__;
  return raw && typeof raw === 'object' ? (raw as RuntimeEnv) : {};
}

function nonEmpty(v: string | undefined): string | undefined {
  return v && v.trim() !== '' ? v.trim() : undefined;
}

/** Initialises Sentry only when a DSN is configured; a laptop or mock run never sends anything. */
export function initObservability(): boolean {
  const rt = runtimeEnv();
  const dsn = nonEmpty(rt.SENTRY_DSN) ?? nonEmpty(import.meta.env.VITE_SENTRY_DSN);
  if (!dsn) return false;
  const release = nonEmpty(rt.RELEASE);
  Sentry.init({
    dsn,
    environment: nonEmpty(rt.ENVIRONMENT) ?? import.meta.env.MODE,
    ...(release ? { release: `web@${release}` } : {}),
    sendDefaultPii: false,
    tracesSampleRate: 0,
  });
  return true;
}

/** Tag events with the signed-in company/user (no PII beyond ids). Safe to call when Sentry is off. */
export function setObservabilityUser(user: { id: string; companyId: string; role: string } | null): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id });
  Sentry.setTags({ companyId: user.companyId, role: user.role });
}
